import EmptyState from '@/components/ui/EmptyState'
import { useAuth } from '@/hooks/useAuth'
import { useAuthModal } from '@/contexts/AuthModalContext'
import { useUnreadMessages } from '@/contexts/MessagesContext'
import type { ChatMessage, MessagePeer } from '@/lib/messages'
import { invokeMessages, messagesError } from '@/lib/messages'
import { getStorePath } from '@/lib/storeSlug'
import { formatRelativeTime } from '@/lib/time'
import {ArrowLeft, Mail, Send} from 'lucide-react'
import { FormEvent, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useParams } from 'react-router-dom'

function MessageThread() {
  const { peerId = '' } = useParams()
  const { user, isAuthenticated } = useAuth()
  const { openAuth } = useAuthModal()
  const { refreshUnread, setUnreadCount } = useUnreadMessages()
  const [peer, setPeer] = useState<MessagePeer | null>(null)
  const [conversationId, setConversationId] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const bottomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages.length])

  useEffect(() => {
    if (!isAuthenticated || !peerId) {
      setLoading(false)
      return
    }

    let active = true
    const loadThread = async (silent = false) => {
      if (!silent) setLoading(true)
      try {
        const result = await invokeMessages<{
          conversation?: { _id?: string; unreadCount?: number } | null
          peer?: MessagePeer
          messages?: ChatMessage[]
        }>({ action: 'thread', peerId })
        if (!active) return
        setPeer(result?.peer || null)
        setMessages(result?.messages || [])
        const nextId = String(result?.conversation?._id || '')
        setConversationId(nextId)
        if (nextId) {
          const pending = Number(result?.conversation?.unreadCount || 0)
          if (pending > 0) {
            await invokeMessages({ action: 'markRead', conversationId: nextId, peerId })
            setUnreadCount(count => Math.max(0, count - pending))
          }
          await refreshUnread()
        }
      } catch (error) {
        if (active && !silent) toast.error(messagesError(error))
      } finally {
        if (active && !silent) setLoading(false)
      }
    }

    void loadThread()
    const timer = window.setInterval(() => {
      void loadThread(true)
    }, 12000)
    return () => {
      active = false
      window.clearInterval(timer)
    }
  }, [isAuthenticated, peerId, refreshUnread, setUnreadCount])

  const handleSend = async (event: FormEvent) => {
    event.preventDefault()
    const nextText = text.trim()
    if (!nextText || sending) return
    setSending(true)
    try {
      const result = await invokeMessages<{ message?: ChatMessage; conversationId?: string }>({
        action: 'send',
        text: nextText,
        peerId,
        conversationId,
      })
      if (result?.message) {
        setMessages(current => [...current, result.message as ChatMessage])
      }
      if (result?.conversationId) setConversationId(result.conversationId)
      setText('')
    } catch (error) {
      toast.error(messagesError(error))
    } finally {
      setSending(false)
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <EmptyState
          icon={Mail}
          title="Sign in to keep chatting"
          description="You need an account to send private messages to this shop."
          action={
            <button
              type="button"
              onClick={openAuth}
              className="rounded-full bg-[#FFDE00] px-5 py-2.5 text-sm font-extrabold text-black shadow-[0_4px_0_#c9a800]"
            >
              Sign in
            </button>
          }
        />
      </div>
    )
  }

  if (user?.userId && peerId && user.userId === peerId) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <EmptyState
          icon={Mail}
          title="That's your shop"
          description="Message other collectors from their public store page."
          action={
            <Link to="/messages" className="rounded-full bg-[#FFDE00] px-5 py-2.5 text-sm font-extrabold text-black">
              Back to inbox
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="mx-auto flex min-h-[calc(100vh-10rem)] max-w-2xl flex-col px-4 py-6 pb-24 sm:px-6 sm:pb-10">
      <div className="mb-4 flex items-center gap-3">
        <Link
          to="/messages"
          className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white/15 text-white hover:bg-white/10"
          aria-label="Back to inbox"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="h-11 w-11 overflow-hidden rounded-full border-2 border-[#FFDE00]/70 bg-[#3B4CCA]">
          {peer?.avatarUrl ? (
            <img src={peer.avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center font-extrabold text-white">
              {(peer?.storeName || '?')[0]?.toUpperCase()}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <h1 className="truncate font-display text-xl font-extrabold text-white">
            {peer?.storeName || 'Collector'}
          </h1>
          {peer?.userId ? (
            <Link to={getStorePath(peer)} className="text-xs font-extrabold text-[#FFDE00]">
              View store
            </Link>
          ) : null}
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto rounded-[28px] border-2 border-white/10 bg-black/40 p-3 sm:p-4">
        {loading ? (
          <div className="space-y-3">
            <div className="h-12 w-2/3 animate-pulse rounded-2xl bg-white/5" />
            <div className="ml-auto h-12 w-1/2 animate-pulse rounded-2xl bg-white/5" />
          </div>
        ) : messages.length === 0 ? (
          <p className="py-10 text-center text-sm font-semibold text-white/45">
            No messages yet. Say hello about a listing or trade.
          </p>
        ) : (
          messages.map(message => {
            const mine = message.senderId === user?.userId
            return (
              <div key={message._id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[82%] rounded-3xl px-4 py-2.5 ${
                    mine
                      ? 'bg-[#FFDE00] text-black shadow-[0_3px_0_#c9a800]'
                      : 'border border-white/10 bg-white/10 text-white'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words text-sm font-bold">{message.text}</p>
                  <p className={`mt-1 text-[10px] font-extrabold ${mine ? 'text-black/50' : 'text-white/40'}`}>
                    {formatRelativeTime(message.createdAt)}
                  </p>
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={handleSend} className="sticky bottom-0 mt-3 flex gap-2 bg-black/80 pb-2 pt-1 backdrop-blur-md">
        <input
          value={text}
          onChange={event => setText(event.target.value)}
          maxLength={2000}
          placeholder={`Message ${peer?.storeName || 'this shop'}`}
          className="w-full rounded-full border-2 border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-white outline-none placeholder:text-white/35 focus:border-[#FFDE00]"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#FFDE00] text-black shadow-[0_4px_0_#c9a800] disabled:opacity-50"
          aria-label="Send message"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  )
}

export default MessageThread
